import styled from "@emotion/styled";

export const DebugPanelContainer = styled.div`
  --debug-bg: #1e1e2e;
  --debug-bg-light: #2a2a3e;
  --debug-bg-lighter: #363650;
  --debug-border: #3d3d5c;
  --debug-text: #e0e0e0;
  --debug-text-muted: #888;
  --debug-accent: #7c7cff;
  --debug-accent-hover: #9d9dff;
  --debug-success: #4ade80;
  --debug-warning: #fbbf24;
  --debug-error: #f87171;
  --debug-info: #60a5fa;

  background: var(--debug-bg);
  color: var(--debug-text);
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  font-size: 12px;
  line-height: 1.5;
  height: 100%;
  overflow-y: auto;
  padding: 16px;

  * {
    box-sizing: border-box;
  }

  a {
    color: var(--debug-accent);
    text-decoration: none;
    &:hover {
      color: var(--debug-accent-hover);
      text-decoration: underline;
    }
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--debug-border);
`;

export const PanelTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: var(--debug-text);
`;

export const Section = styled.details`
  background: var(--debug-bg-light);
  border: 1px solid var(--debug-border);
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;

  &[open] > summary {
    border-bottom: 1px solid var(--debug-border);
  }
`;

export const SectionHeader = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  font-size: 12px;
  color: var(--debug-text);
  background: var(--debug-bg-light);

  &:hover {
    background: var(--debug-bg-lighter);
  }

  &::-webkit-details-marker {
    display: none;
  }

  &::before {
    content: "";
    display: inline-block;
    width: 0;
    height: 0;
    border-left: 5px solid var(--debug-text-muted);
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    margin-right: 8px;
    transition: transform 0.15s ease;
  }

  details[open] > &::before {
    transform: rotate(90deg);
  }
`;

export const SectionContent = styled.div`
  padding: 12px;
  background: var(--debug-bg);
`;

export const SectionTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background: var(--debug-bg-lighter);
  color: var(--debug-text-muted);
`;

export const CodeBlock = styled.pre`
  background: var(--debug-bg-lighter);
  border: 1px solid var(--debug-border);
  border-radius: 4px;
  padding: 10px;
  margin: 8px 0;
  overflow-x: auto;
  font-size: 11px;
  line-height: 1.4;
  color: var(--debug-text);
  white-space: pre-wrap;
  word-break: break-word;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const KeyValue = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
  border-bottom: 1px solid var(--debug-border);

  &:last-child {
    border-bottom: none;
  }
`;

export const Key = styled.span`
  color: var(--debug-text-muted);
  min-width: 80px;
  flex-shrink: 0;
  font-size: 11px;
`;

export const Value = styled.span`
  color: var(--debug-text);
  word-break: break-word;
  font-size: 11px;
`;

export const StepItem = styled.div`
  padding: 10px;
  margin-bottom: 6px;
  border-radius: 4px;
  background: var(--debug-bg-light);
  border: 1px solid var(--debug-border);

  &[data-unused="true"] {
    opacity: 0.8;
  }

  &[data-active="true"] {
    border-left: 3px solid var(--debug-success);
    background: color-mix(in srgb, var(--debug-success) 7%, transparent);
  }

  &[data-finish="true"] {
    border-right: 3px solid var(--debug-error);
  }

  &:hover {
    background: var(--debug-bg-lighter);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StepTitle = styled.h4`
  margin: 0 0 6px 0;
  font-size: 12px;
  font-weight: 500;
  color: var(--debug-text);
`;

export const StepMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 10px;
  color: var(--debug-text-muted);
`;

export const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const MetaLabel = styled.span`
  color: var(--debug-text-muted);
`;

export const MetaValue = styled.code`
  color: var(--debug-accent);
  background: var(--debug-bg-lighter);
  padding: 1px 4px;
  border-radius: 3px;
`;

export const FormGroup = styled.div`
  margin-bottom: 12px;
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 11px;
  color: var(--debug-text-muted);
  margin-bottom: 4px;
  font-weight: 500;
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  background: var(--debug-bg-lighter);
  border: 1px solid var(--debug-border);
  border-radius: 4px;
  padding: 8px;
  font-family: inherit;
  font-size: 11px;
  color: var(--debug-text);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--debug-accent);
  }
`;

export const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 11px;
  padding: 4px 0;
  color: var(--debug-text);

  input {
    appearance: none;
    width: 32px;
    height: 18px;
    background: var(--debug-bg-lighter);
    border: 1px solid var(--debug-border);
    border-radius: 9px;
    cursor: pointer;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 12px;
      height: 12px;
      background: var(--debug-text-muted);
      border-radius: 50%;
      transition: all 0.2s ease;
    }

    &:checked {
      background: var(--debug-accent);
      border-color: var(--debug-accent);

      &::after {
        left: 16px;
        background: white;
      }
    }
  }
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--debug-accent);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: var(--debug-accent-hover);
  }
`;

export const DangerButton = styled(ActionButton)`
  background: var(--debug-error);

  &:hover {
    background: #ef4444;
  }
`;

export const VariableItem = styled.div`
  padding: 8px;
  background: var(--debug-bg-light);
  border: 1px solid var(--debug-border);
  border-radius: 4px;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const VariableName = styled.code`
  color: var(--debug-accent);
  font-weight: 500;
  font-size: 11px;
`;

export const VariableType = styled.span`
  color: var(--debug-text-muted);
  font-size: 10px;
  margin-left: 8px;
`;

export const VariableValue = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: var(--debug-text);
  word-break: break-word;
`;

export const EndScreenItem = styled.div`
  padding: 8px 10px;
  background: var(--debug-bg-light);
  border: 1px solid var(--debug-border);
  border-radius: 4px;
  margin-bottom: 6px;

  &[data-active="true"] {
    border-left: 3px solid var(--debug-warning);
    background: color-mix(in srgb, var(--debug-warning) 7%, transparent);
  }

  &:hover {
    background: var(--debug-bg-lighter);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--debug-border);
`;

export const StepCount = styled.div`
  font-size: 11px;
  color: var(--debug-text-muted);
  padding: 8px 0;
`;

export const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--debug-accent);
  font-size: 11px;

  &:hover {
    color: var(--debug-accent-hover);
  }
`;

export const NoLogic = styled.div`
  color: var(--debug-text-muted);
  font-size: 11px;
  font-style: italic;
  padding: 12px;
  text-align: center;
`;

export const LogicActionItem = styled.div`
  padding: 10px;
  margin-bottom: 8px;
  background: var(--debug-bg-light);
  border: 1px solid var(--debug-border);
  border-radius: 6px;
  border-left: 3px solid var(--debug-accent);
  position: relative;

  &[data-action="jump"] {
    border-left-color: var(--debug-info);
  }
  &[data-action="set"] {
    border-left-color: var(--debug-success);
  }
  &[data-action="add"] {
    border-left-color: var(--debug-warning);
  }

  &[data-trigger="true"] {
    background: color-mix(in srgb, var(--debug-success) 8%, transparent);
    border-color: color-mix(in srgb, var(--debug-success) 27%, transparent);
    border-left-color: var(--debug-success);
  }

  &[data-skipped="true"] {
    opacity: 0.8;
    background: var(--debug-bg);
    border-color: color-mix(in srgb, var(--debug-border) 40%, transparent);
    border-left-color: color-mix(
      in srgb,
      var(--debug-text-muted) 27%,
      transparent
    );

    &::after {
      content: "SKIPPED";
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: var(--debug-text-muted);
      background: var(--debug-bg-lighter);
      padding: 2px 6px;
      border-radius: 3px;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const LogicActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

export const LogicActionType = styled.span`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--debug-bg-lighter);
  color: var(--debug-text-muted);

  &[data-action="jump"] {
    background: color-mix(in srgb, var(--debug-info) 20%, transparent);
    color: var(--debug-info);
  }
  &[data-action="set"] {
    background: color-mix(in srgb, var(--debug-success) 20%, transparent);
    color: var(--debug-success);
  }
  &[data-action="add"] {
    background: color-mix(in srgb, var(--debug-warning) 20%, transparent);
    color: var(--debug-warning);
  }
`;

export const LogicDescription = styled.div`
  font-size: 12px;
  color: var(--debug-text);
  font-weight: 500;
`;

export const LogicCondition = styled.div`
  font-size: 11px;
  color: var(--debug-text-muted);
  margin-top: 6px;
  padding: 6px 8px;
  background: var(--debug-bg);
  border-radius: 4px;
`;

export const ConditionToken = styled.span`
  position: relative;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
  cursor: help;
  background: transparent;
  color: var(--debug-text-muted);

  &[data-type="text"],
  &[data-type="operator"] {
    padding: 0;
    cursor: default;
  }

  &[data-type="field"] {
    background: color-mix(in srgb, var(--debug-info) 13%, transparent);
    color: var(--debug-info);
  }
  &[data-type="variable"] {
    background: color-mix(in srgb, var(--debug-success) 13%, transparent);
    color: var(--debug-success);
  }
  &[data-type="hidden"] {
    background: color-mix(in srgb, var(--debug-warning) 13%, transparent);
    color: var(--debug-warning);
  }
  &[data-type="value"] {
    background: color-mix(in srgb, var(--debug-accent) 13%, transparent);
    color: var(--debug-accent);
  }

  &[data-match="true"] {
    text-decoration: underline;
    text-decoration-color: var(--debug-success);
    text-underline-offset: 2px;
  }

  &[data-match="false"] {
    opacity: 0.8;
  }

  &:hover > span {
    visibility: visible;
    opacity: 1;
  }
`;

export const TokenTooltip = styled.span`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--debug-bg-lighter);
  border: 1px solid var(--debug-border);
  color: var(--debug-text);
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 1000;
  transition:
    opacity 0.15s ease,
    visibility 0.15s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: var(--debug-border);
  }
`;
